import { Edit, MoreVert, Password, Send } from "@mui/icons-material";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTranslations } from "adapters/i18n";
import React, { type MouseEvent, useState } from "react";
import type { User } from "../userList.types";

type UserActionsMenuProps = {
  user: User;
  onCopyLink: (user: User) => Promise<void>;
  onEdit: (user: User) => void;
  onSendLink: (user: User) => Promise<void>;
};

const UserActionsMenu = ({
  user,
  onCopyLink,
  onSendLink,
  onEdit,
}: UserActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const shouldOpen = Boolean(anchorEl);

  const handleClick = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton data-testid="user-actions-btn" onClick={handleClick}>
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="user-list-actions"
        open={shouldOpen}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{ sx: { maxWidth: 250 } }}
      >
        <EditUserItem onEdit={onEdit} user={user} />
        <CopyPasswordLinkItem onCopyLink={onCopyLink} user={user} />
        <SendPasswordLinkItem onSendLink={onSendLink} user={user} />
      </Menu>
    </>
  );
};

type EditUserItemProps = {
  onEdit: (user: User) => void;
  user: User;
};

const EditUserItem = ({ onEdit, user }: EditUserItemProps) => {
  const { t } = useTranslations();

  const handleClick = () => {
    onEdit(user);
  };

  return (
    <MenuItem
      sx={{ minHeight: 48 }}
      onClick={handleClick}
      data-testid="edit-user-action"
    >
      <ListItemIcon>
        <Edit />
      </ListItemIcon>
      <ListItemText primary={t("common:actions.edit")} />
    </MenuItem>
  );
};

type CopyPasswordLinkItemProps = {
  onCopyLink: (user: User) => Promise<void>;
  user: User;
};

const CopyPasswordLinkItem = ({
  onCopyLink,
  user,
}: CopyPasswordLinkItemProps) => {
  const { t } = useTranslations();

  const handleClick = () => {
    onCopyLink(user);
  };

  return (
    <MenuItem
      data-testid="copy-password-link-action"
      sx={{ minHeight: 48 }}
      onClick={handleClick}
    >
      <ListItemIcon>
        <Password />
      </ListItemIcon>
      <ListItemText
        sx={{ textWrap: "wrap" }}
        primary={t("user:generate-and-copy-password-link")}
      />
    </MenuItem>
  );
};

type SendPasswordLinkItemProps = {
  onSendLink: (user: User) => Promise<void>;
  user: User;
};

const SendPasswordLinkItem = ({
  onSendLink,
  user,
}: SendPasswordLinkItemProps) => {
  const { t } = useTranslations();

  const handleClick = () => {
    onSendLink(user);
  };

  return (
    <MenuItem
      data-testid="send-password-link-action"
      sx={{ minHeight: 48 }}
      onClick={handleClick}
    >
      <ListItemIcon>
        <Send />
      </ListItemIcon>
      <ListItemText
        sx={{ textWrap: "wrap" }}
        primary={t("user:send-password-link")}
      />
    </MenuItem>
  );
};

export default UserActionsMenu;
